<script lang="ts">
	//Services
	import { format } from '../../../../services/i18n';
	import { gen2Request } from '../../../../services/gen_2_http_reqs';
	//Helpers
	import {
		get_device_component_index,
		get_gen_2_component_channel_structure,
		get_gen_2_component_from_category,
		isDimmerWithTransitionMinLimitations,
		real_id,
	} from '../../../../helpers/interface_helpers';
	import { getErrorOrInfoMessageForMinAndMax } from '../../../../helpers/utilities';
	//Stores
	import { devices_settings } from 'src/stores/device-runtime/status';
	//Types
	import type { SettingRequest } from '../../../../types/requests/settings/setting';
	//Design system components
	import { Alert, Button, Input, toast } from '@Shelly/shelly-design-system';
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';

	export let device;

	let id = real_id(device.id);

	let componentIndex = get_device_component_index(device);

	const min = isDimmerWithTransitionMinLimitations(device.type) ? 1 : 0;
	const max = 5;

	// Local variable to hold the input value, synced with store
	let inputValue: string = '';
	let lastStoreValue: number | undefined;

	function syncInputFromStore() {
		const storeValue =
			devices_settings.getByRealId(id)?.[`${get_gen_2_component_channel_structure(device)}`]
				?.transition_duration;
		if (storeValue !== undefined && storeValue !== null && storeValue !== lastStoreValue) {
			lastStoreValue = storeValue;
			inputValue = String(storeValue);
		} else if (storeValue === undefined || storeValue === null) {
			if (lastStoreValue === undefined) {
				inputValue = '';
			}
		}
	}
	$: (inputValue, lastStoreValue, void syncInputFromStore());

	let loading = false;

	$: error = getErrorOrInfoMessageForMinAndMax(
		inputValue === '' ? undefined : Number(inputValue),
		min,
		max,
	).error;

	const validate = () => {
		const numValue = inputValue === '' ? NaN : Number(inputValue);
		if (!isNaN(numValue)) {
			if (numValue > max) inputValue = String(max);
			if (numValue < min) inputValue = String(min);
		}
	};
	const setDefaultTransition = async () => {
		const numValue = inputValue === '' ? NaN : Number(inputValue);
		if (isNaN(numValue) || numValue === lastStoreValue) return;

		loading = true;
		if (numValue < min || numValue > max) {
			loading = false;
			return toast.open({
				type: 'danger',
				title:
					$format('movement_limit_toast', {
						values: { min: min, max: max },
					}) || 'Values must be between min - max!',
			});
		}

		const params = {
			id: componentIndex,
			config: {
				transition_duration: numValue,
			},
		};

		const res: SettingRequest = await gen2Request(
			device,
			`${get_gen_2_component_from_category(device)}.SetConfig`,
			params,
		);
		const response = onSettingsResponse(res, device);
		if (response) {
			const settings = devices_settings.getByRealId(id);
			settings[`${get_gen_2_component_channel_structure(device)}`].transition_duration = numValue;
			devices_settings.update_devices_settings(id, settings);
			lastStoreValue = numValue;
		} else {
			inputValue = lastStoreValue !== undefined ? String(lastStoreValue) : '';
		}
		loading = false;
	};

	$: inputData = getErrorOrInfoMessageForMinAndMax(
		inputValue === '' ? undefined : Number(inputValue),
		min,
		max,
	);
</script>

<Alert
	class="mb-s"
	heading={$format('alert_heading_default_transition', {
		default: `Transition time (in seconds) when transition is not explicitly set.`,
	})}
/>
<Input
	size="m"
	bind:value={inputValue}
	type={'number'}
	{min}
	{max}
	error={inputData.error}
	info={inputData.info}
	step={0.1}
	on:input={validate}
	on:enter={setDefaultTransition}
	id="input-1dx3ss2"
	aria-label={$format('alert_heading_default_transition', {
		default: 'Default transition time',
	})}
/>
<Button class="mt-s" {loading} disabled={error} size="m" width="full" on:click={setDefaultTransition}
	>{$format('button_save')}</Button
>
